import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosRequestHeaders, type AxiosResponse } from 'axios';

type Identifier = string | number;

export interface ListParams {
    [key: string]: string | number | boolean | Array<string | number | boolean> | undefined;
}

const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'X-XSRF-TOKEN';

const hasBrowserEnvironment = (): boolean => typeof document !== 'undefined';

const readCookie = (name: string): string | null => {
    if (!hasBrowserEnvironment()) {
        return null;
    }

    return (
        document.cookie
            .split(';')
            .map((cookie) => cookie.trim())
            .find((cookie) => cookie.startsWith(`${name}=`))
            ?.substring(name.length + 1) ?? null
    );
};

/**
 * Lightweight wrapper around axios that scopes requests to /api,
 * adds credentials, and returns the response data shape directly.
 */
export class ApiClient {
    private readonly http: AxiosInstance;
    private csrfInitialized = false;
    private csrfPromise: Promise<void> | null = null;

    constructor(baseURL = '/api') {
        this.http = axios.create({
            baseURL,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            withCredentials: true,
            xsrfCookieName: 'XSRF-TOKEN',
            xsrfHeaderName: 'X-XSRF-TOKEN',
        });

        this.http.interceptors.request.use((config) => {
            const token = readCookie(XSRF_COOKIE);

            if (token) {
                const headers = (config.headers ?? {}) as AxiosRequestHeaders;
                headers[XSRF_HEADER] = decodeURIComponent(token);
                config.headers = headers;
            }

            return config;
        });

        this.http.interceptors.response.use(
            (response) => response,
            (error) => {
                const status = error?.response?.status;
                const data = error?.response?.data;

                if (status === 402 && data?.billing_required && hasBrowserEnvironment()) {
                    window.dispatchEvent(new CustomEvent('billing-required'));
                }

                return Promise.reject(error);
            },
        );
    }

    public get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ url, method: 'get', ...config });
    }

    public post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ url, method: 'post', data, ...config });
    }

    public put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ url, method: 'put', data, ...config });
    }

    public patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ url, method: 'patch', data, ...config });
    }

    public delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ url, method: 'delete', ...config });
    }

    public resource<T = unknown>(name: string): ResourceService<T> {
        return new ResourceService<T>(this, name);
    }

    private async request<T>(config: AxiosRequestConfig): Promise<T> {
        await this.ensureCsrfCookie();
        const response: AxiosResponse<T> = await this.http.request<T>(config);
        return response.data;
    }

    private async ensureCsrfCookie(): Promise<void> {
        if (this.csrfInitialized) {
            return;
        }

        const hasExistingCookie = Boolean(readCookie(XSRF_COOKIE));

        if (hasExistingCookie) {
            this.csrfInitialized = true;
            return;
        }

        if (!this.csrfPromise) {
            this.csrfPromise = axios
                .get('/sanctum/csrf-cookie', {
                    withCredentials: true,
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                })
                .then(() => {
                    this.csrfInitialized = true;
                })
                .finally(() => {
                    this.csrfPromise = null;
                });
        }

        await this.csrfPromise;
    }
}

/**
 * Convenience layer for common CRUD interactions with a RESTful resource.
 */
export class ResourceService<T = unknown> {
    constructor(
        private readonly client: ApiClient,
        private readonly resource: string,
    ) {}

    public list(params?: ListParams): Promise<T[]> {
        return this.client.get<T[]>(`/${this.resource}`, { params });
    }

    public show(id: Identifier, params?: ListParams): Promise<T> {
        return this.client.get<T>(`/${this.resource}/${id}`, { params });
    }

    public create(payload: Partial<T>): Promise<T> {
        return this.client.post<T>(`/${this.resource}`, payload);
    }

    public update(id: Identifier, payload: Partial<T>): Promise<T> {
        return this.client.put<T>(`/${this.resource}/${id}`, payload);
    }

    public delete(id: Identifier): Promise<void> {
        return this.client.delete<void>(`/${this.resource}/${id}`);
    }
}

export const apiClient = new ApiClient('/api');
export const webClient = new ApiClient('/');
